import { gql } from "@apollo/client";
import Grid from "atoms/Grid";
import usePagedData from "helpers/usePagedData";
import { ALL_TEAMS } from "molecules/PlayerList";
import Section from "molecules/Section";
import ShowMoreButton from "molecules/ShowMoreButton";
import HighlightPlayerDialog from "pages/video_player/HighlightPlayerDialog";
import React, { useEffect, useState } from "react";

import EventCard, { EventCardFragment } from "./EventCard";

export const GameOverviewHighlightsFragment = gql`
  fragment GameOverviewHighlightsFragment on Game {
    id
  }
`;

const PLAYER_HIGHLIGHTS_QUERY = gql`
  query GameOverviewHighlights_player($gamePlayerId: ID!, $offset: Int, $limit: Int) {
    gamePlayer(id: $gamePlayerId) {
      id
      events(offset: $offset, limit: $limit) {
        id
        ...EventCardFragment
      }
      eventsCount
    }
  }
  ${EventCardFragment}
`;

const TEAM_HIGHLIGHTS_QUERY = gql`
  query GameOverviewHighlights_team($gameId: ID!, $teamId: ID!, $offset: Int, $limit: Int) {
    game(id: $gameId) {
      id
      participation(teamId: $teamId) {
        id
        events(offset: $offset, limit: $limit) {
          id
          ...EventCardFragment
        }
        eventsCount
      }
    }
  }
  ${EventCardFragment}
`;

const GAME_HIGHLIGHTS_QUERY = gql`
  query GameOverviewHighlights_game($gameId: ID!, $offset: Int, $limit: Int) {
    game(id: $gameId) {
      id
      events(offset: $offset, limit: $limit) {
        id
        ...EventCardFragment
      }
      eventsCount
    }
  }
  ${EventCardFragment}
`;

function highlightsSource({ gameId, gamePlayer, teamId }) {
  if (gamePlayer) {
    return {
      query: PLAYER_HIGHLIGHTS_QUERY,
      variables: { gamePlayerId: gamePlayer.id },
      selectPage: (data) => ({ items: data.gamePlayer?.events, total: data.gamePlayer?.eventsCount }),
    };
  }
  if (teamId === ALL_TEAMS) {
    return {
      query: GAME_HIGHLIGHTS_QUERY,
      variables: { gameId },
      selectPage: (data) => ({ items: data.game?.events, total: data.game?.eventsCount }),
    };
  }
  return {
    query: TEAM_HIGHLIGHTS_QUERY,
    variables: { gameId, teamId },
    selectPage: (data) => ({
      items: data.game?.participation?.events,
      total: data.game?.participation?.eventsCount,
    }),
  };
}

const HIGHLIGHT_WIDTH = 160;
// Same on every screen width: two rows of 6 on desktop
const HIGHLIGHTS_PER_PAGE = 12;

// Remount (key) on every team or player change so the loaded pages start over.
export default function GameOverviewHighlights({ gameId, gamePlayer, teamId }) {
  const { query, variables, selectPage } = highlightsSource({ gameId, gamePlayer, teamId });
  const { items, remaining, loading, loadMore } = usePagedData(query, variables, selectPage, {
    pageSize: HIGHLIGHTS_PER_PAGE,
  });
  // index into all the highlights of the list, open in the dialog; null when closed
  const [openIndex, openIndexSet] = useState(null);
  const total = items.length + remaining;

  // Next past the last loaded highlight loads the next page; the dialog waits for it
  const needsMore = openIndex !== null && openIndex >= items.length && remaining > 0;
  useEffect(() => {
    if (needsMore && !loading) loadMore();
  }, [needsMore, loading]);

  if (!items.length) {
    return null;
  }

  return (
    <Section title="HIGHLIGHTS">
      <Grid gridItemWidth={`${HIGHLIGHT_WIDTH}px`}>
        {items.map((event, index) => (
          <EventCard key={event.id} event={event} onOpen={() => openIndexSet(index)} />
        ))}
      </Grid>
      <HighlightPlayerDialog
        open={openIndex !== null}
        onClose={() => openIndexSet(null)}
        eventId={openIndex !== null ? items[openIndex]?.id : undefined}
        index={openIndex}
        total={total}
        onPrevious={openIndex > 0 ? () => openIndexSet(openIndex - 1) : undefined}
        onNext={openIndex !== null && openIndex < total - 1 ? () => openIndexSet(openIndex + 1) : undefined}
      />
      <ShowMoreButton remaining={remaining} noun="highlight" onClick={loadMore} disabled={loading} />
    </Section>
  );
}
